import { FC, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import classNames from 'classnames'
import ArrowRightLineIcon from '@rsuite/icons/ArrowRightLine'
import ReloadIcon from '@rsuite/icons/Reload'
import useAuth from 'hooks/Auth'
import { ROUTES } from 'routers/Routes'
import {
  useGetClientNameQuery,
  useGetDocumentDashboardQuery,
  useGetDocumentsQuery
} from 'pages/documents/documentsApiSlice'
import PageHeader from 'pages/documents/components/PageHeader'
import EmptyState from 'pages/documents/components/EmptyState'
import StatusBadge from 'pages/documents/components/StatusBadge'
import { documentPath } from 'pages/documents/documentNav'
import {
  documentTitle,
  errorMessage,
  formatAmount,
  formatDateTime
} from 'pages/documents/utils'
import { DashboardCounts } from 'pages/documents/types'
import VolumeChart, { OUTCOMES } from './components/VolumeChart'
import { toBuckets } from './buckets'
import 'pages/documents/documents.scss'
import './dashboard.scss'

const PERIODS = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '12 months' }
]

const percent = (value?: number) =>
  value == null ? '—' : `${Math.round(value * 100)}%`

const compactAmount = (value: number) =>
  value.toLocaleString(undefined, {
    notation: value >= 100000 ? 'compact' : 'standard',
    maximumFractionDigits: value >= 100000 ? 1 : 2,
    minimumFractionDigits: value >= 100000 ? 0 : 2
  })

type Tile = {
  key: string
  label: string
  value: string
  note: string
  tone?: 'warning'
  to?: string
}

/** Small proportional bar of outcomes for a table row. */
const OutcomeStrip: FC<{ counts: DashboardCounts }> = ({ counts }) => (
  <span className="db-strip" aria-hidden>
    {OUTCOMES.filter((o) => counts[o.key] > 0).map((o) => (
      <span
        key={o.key}
        className={`db-seg-fill db-seg-fill--${o.tone}`}
        style={{ flexGrow: counts[o.key] }}
      />
    ))}
  </span>
)

/**
 * Operations overview: what arrived, how much went through untouched, what
 * needs a person, and what was delivered, for one client or all of them.
 */
const Dashboard: FC = () => {
  const navigate = useNavigate()
  const { selectedClientId, setSelectedClientId } = useAuth()
  const [days, setDays] = useState(30)
  const [allClients, setAllClients] = useState(!selectedClientId)
  const scopeClientId = allClients ? undefined : (selectedClientId ?? undefined)

  const dashboard = useGetDocumentDashboardQuery({
    clientId: scopeClientId,
    days
  })
  const clientName = useGetClientNameQuery(
    { clientId: selectedClientId ?? '' },
    { skip: !selectedClientId }
  ).data
  const attention = useGetDocumentsQuery(
    {
      clientId: selectedClientId ?? '',
      queue: 'NEEDS_REVIEW',
      page: 0,
      size: 5,
      sort: 'receivedAt',
      direction: 'desc'
    },
    { skip: !scopeClientId }
  )

  const data = dashboard.currentData
  const buckets = useMemo(() => (data ? toBuckets(data.daily) : []), [data])
  const totals = data?.totals
  const attentionCount = totals ? totals.needsReview + totals.failed : 0
  const maxSupplier = Math.max(
    1,
    ...(data?.topSuppliers ?? []).map((s) => s.totalAmount ?? 0)
  )
  const scopeLabel = scopeClientId
    ? (clientName ?? 'This client')
    : 'All clients'
  const periodLabel = PERIODS.find((p) => p.days === days)?.label

  const openClientDocuments = (clientId: number) => {
    setSelectedClientId(String(clientId))
    navigate(ROUTES.DOCUMENTS)
  }

  const tiles: Tile[] = totals
    ? [
        {
          key: 'received',
          label: 'Documents received',
          value: totals.received.toLocaleString(),
          note: `in the last ${periodLabel}`
        },
        {
          key: 'automation',
          label: 'Completed automatically',
          value: percent(data?.automationRate),
          note: `${totals.completed.toLocaleString()} completed without help`
        },
        {
          key: 'attention',
          label: 'Need attention',
          value: attentionCount.toLocaleString(),
          note: `${totals.needsReview} to review · ${totals.failed} failed`,
          tone: attentionCount > 0 ? 'warning' : undefined,
          to: scopeClientId
            ? `${ROUTES.DOCUMENTS}?queue=NEEDS_REVIEW`
            : undefined
        },
        {
          key: 'delivered',
          label: 'Amount delivered',
          value: compactAmount(data?.amountDelivered ?? 0),
          note: `${data?.remittancesDelivered ?? 0} remittance${
            data?.remittancesDelivered === 1 ? '' : 's'
          } sent on`
        },
        {
          key: 'processing',
          label: 'In processing',
          value: totals.processing.toLocaleString(),
          note: 'queued or being read'
        }
      ]
    : []

  const renderTile = (t: Tile) => {
    const body = (
      <>
        <span className="db-tile__label">{t.label}</span>
        <span className="db-tile__value">{t.value}</span>
        <span className="db-tile__note">{t.note}</span>
      </>
    )
    const className = classNames('db-tile', t.tone && `db-tile--${t.tone}`)
    return t.to ? (
      <Link key={t.key} to={t.to} className={`${className} db-tile--link`}>
        {body}
        <ArrowRightLineIcon className="db-tile__go" />
      </Link>
    ) : (
      <div key={t.key} className={className}>
        {body}
      </div>
    )
  }

  const renderChart = () => {
    if (!data) {
      return (
        <div className="dx-skeleton db-chart-skeleton" aria-busy>
          <span />
        </div>
      )
    }
    if (data.totals.received === 0) {
      return (
        <EmptyState
          compact
          title="No documents in this period"
          description="Try a longer period, or check the client’s inbox is set up."
        />
      )
    }
    return <VolumeChart buckets={buckets} title="Documents by outcome" />
  }

  const renderAttention = () => {
    const rows = attention.currentData?.content ?? []
    if (rows.length) {
      return (
        <ul className="db-list">
          {rows.map((doc) => (
            <li key={doc.id}>
              <Link to={documentPath(doc)} className="db-list__row">
                <span className="db-list__main">
                  <span className="dx-strong">{documentTitle(doc)}</span>
                  <span className="dx-sub">
                    {formatDateTime(doc.receivedAt)}
                  </span>
                </span>
                <StatusBadge doc={doc} />
              </Link>
            </li>
          ))}
        </ul>
      )
    }
    if (attention.isFetching) {
      return (
        <div className="dx-skeleton" aria-busy>
          <span style={{ width: '70%' }} />
          <span style={{ width: '55%' }} />
        </div>
      )
    }
    return (
      <EmptyState
        compact
        title="Nothing to review"
        description="Every document has been read cleanly."
      />
    )
  }

  return (
    <div className="dx db">
      <PageHeader
        title="Dashboard"
        subtitle={`Document AI activity · ${scopeLabel}`}
        actions={
          <div className="db-filters">
            {selectedClientId && (
              <div className="db-segmented" role="group" aria-label="Scope">
                <button
                  type="button"
                  aria-pressed={!allClients}
                  className={classNames({ 'is-active': !allClients })}
                  onClick={() => setAllClients(false)}
                >
                  {clientName ?? 'This client'}
                </button>
                <button
                  type="button"
                  aria-pressed={allClients}
                  className={classNames({ 'is-active': allClients })}
                  onClick={() => setAllClients(true)}
                >
                  All clients
                </button>
              </div>
            )}
            <div className="db-segmented" role="group" aria-label="Period">
              {PERIODS.map((p) => (
                <button
                  key={p.days}
                  type="button"
                  aria-pressed={days === p.days}
                  className={classNames({ 'is-active': days === p.days })}
                  onClick={() => setDays(p.days)}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="dx-icon-btn dx-icon-btn--bordered"
              onClick={() => dashboard.refetch()}
              aria-label="Refresh"
              title="Refresh"
            >
              <ReloadIcon spin={dashboard.isFetching} />
            </button>
          </div>
        }
      />

      {dashboard.isError && !dashboard.isFetching ? (
        <div className="dx-card">
          <EmptyState
            tone="danger"
            title="Couldn’t load the dashboard"
            description={errorMessage(dashboard.error, 'Please try again.')}
          />
        </div>
      ) : (
        <>
          <section className="db-tiles" aria-label="Key numbers">
            {tiles.length
              ? tiles.map(renderTile)
              : PERIODS.concat(PERIODS[0]).map((p, i) => (
                  // Placeholders while loading.
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={`${p.days}-${i}`} className="db-tile">
                    <span className="dx-skeleton" aria-busy>
                      <span style={{ width: '60%' }} />
                      <span style={{ width: '40%', height: 22 }} />
                    </span>
                  </div>
                ))}
          </section>

          <section className="dx-card db-card">
            <h2 className="dx-card__title">Documents by outcome</h2>
            {renderChart()}
          </section>

          <div className="db-columns">
            {scopeClientId ? (
              <section className="dx-card db-card">
                <div className="dx-card__head">
                  <h2 className="dx-card__title">Needs review</h2>
                  <Link
                    className="db-more"
                    to={`${ROUTES.DOCUMENTS}?queue=NEEDS_REVIEW`}
                  >
                    View all
                  </Link>
                </div>
                {renderAttention()}
              </section>
            ) : (
              <section className="dx-card db-card">
                <h2 className="dx-card__title">By client</h2>
                {data?.byClient.length ? (
                  <div className="dx-table-wrap">
                    <table className="dx-table dx-table--compact db-clients">
                      <thead>
                        <tr>
                          <th scope="col">Client</th>
                          <th scope="col" className="dx-num">
                            Received
                          </th>
                          <th scope="col">Outcomes</th>
                          <th scope="col" className="dx-num">
                            Need attention
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.byClient.map((c) => (
                          <tr
                            key={c.clientId}
                            className="dx-table__row"
                            tabIndex={0}
                            onClick={() => openClientDocuments(c.clientId)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                openClientDocuments(c.clientId)
                              }
                            }}
                          >
                            <th scope="row" className="dx-strong">
                              {c.clientName}
                            </th>
                            <td className="dx-num">{c.counts.received}</td>
                            <td>
                              <OutcomeStrip counts={c.counts} />
                            </td>
                            <td className="dx-num">
                              {c.counts.needsReview + c.counts.failed}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EmptyState
                    compact
                    title="No client activity in this period"
                  />
                )}
              </section>
            )}

            <section className="dx-card db-card">
              <h2 className="dx-card__title">
                Top suppliers by amount delivered
              </h2>
              {data?.topSuppliers.length ? (
                <ol className="db-suppliers">
                  {data.topSuppliers.map((s) => (
                    <li key={s.name}>
                      <div className="db-suppliers__row">
                        <span className="db-suppliers__name" title={s.name}>
                          {s.name}
                        </span>
                        <span className="db-suppliers__amount">
                          {formatAmount(s.totalAmount)}
                        </span>
                      </div>
                      <span className="db-meter" aria-hidden>
                        <span
                          style={{
                            width: `${Math.max(
                              2,
                              ((s.totalAmount ?? 0) / maxSupplier) * 100
                            )}%`
                          }}
                        />
                      </span>
                      <span className="dx-sub">
                        {s.documents} remittance{s.documents === 1 ? '' : 's'}
                      </span>
                    </li>
                  ))}
                </ol>
              ) : (
                <EmptyState
                  compact
                  title="Nothing delivered in this period"
                  description="Suppliers appear once remittances are saved and sent on."
                />
              )}
            </section>
          </div>
        </>
      )}
    </div>
  )
}

export default Dashboard
